import apiFn from 'common/services/api-service';
import { crop, duplicate, remove } from 'common/services/image-service';

import { TYPE_IMAGE, UNIT_DIVISION, UNIT_BLEED_LENGTH } from './Builder/constants';

const api = eventId =>
  apiFn(`/api/uapi/events/${eventId}/badges`, {
    Accept: 'application/vnd.eventmobi+json; version=p.5',
  });

/**
 * Process badge data from the API, separating fields into:
 * - `badge`: The badge's metadata; anything that is not part of the editor state.
 * - `design`: The badge's content; anything editable and undoable.
 *
 * @returns {{
 *  badge: {
 *    id: string,
 *    name: string,
 *    badgeType: string,
 *    appliedTo: {},
 *    appliedPeopleCount: number,
 *    size: {
 *      width: number,
 *      height: number,
 *      cols: number,
 *      rows: number,
 *      unit: string,
 *      innerBadgeSize: { width: number, height: number }
 *    }
 *    isBleedEnabled: boolean
 *  },
 *  design: {
 *    widgets: Array,
 *    background: { image: { id: null, url: null }, color: string, size: {} },
 *  },
 * }}
 */
export const parseBadgeData = data => {
  const bleedAmt = data.isBleedEnabled ? 2 * UNIT_BLEED_LENGTH[data.size.unitType] : 0;
  const width = Number(data.size.width) + bleedAmt;
  const height = Number(data.size.height) + bleedAmt;

  const badge = {
    id: data.id,
    name: data.name,
    badgeType: data.badgeType,
    appliedTo: data.appliedTo,
    appliedPeopleCount: data.appliedPeopleCount,
    size: {
      width: parseFloat(width),
      height: parseFloat(height),
      cols: width * UNIT_DIVISION[data.size.unitType],
      rows: height * UNIT_DIVISION[data.size.unitType],
      unit: data.size.unitType,
      innerBadgeSize: {
        width: Number(data.size.width),
        height: Number(data.size.height),
      },
    },
    isBleedEnabled: data.isBleedEnabled,
    orientation: data.orientation,
    updatedAt: data.updatedAt,
    previewImage: data.previewImage,
  };

  const { designData } = data;

  const design = {
    widgets: (designData.widgets ?? [])
      .sort((a, b) => a.z - b.z)
      .map(widget => ({
        internalState: {
          isNew: false,
          isCloned: false,
        },
        type: widget.type,
        id: widget.id,
        layout: {
          x: widget.x,
          y: widget.y,
          w: widget.width * UNIT_DIVISION[data.size.unitType],
          h: widget.height * UNIT_DIVISION[data.size.unitType],
        },
        params: {
          image: {
            id: widget?.backgroundImage?.id ?? null,
            url: widget?.backgroundImage?.fullSizeUrl ?? null,
            thumb: widget?.backgroundImage?.thumbnailUrl ?? null,
          },
          foreground: {
            id: widget?.foregroundImage?.id ?? null,
            url: widget?.foregroundImage?.fullSizeUrl ?? null,
            thumb: widget?.foregroundImage?.thumbnailUrl ?? null,
          },
          richText: widget?.foregroundRichText ?? null,
          ...JSON.parse(widget?.configuration ?? null),
        },
      })),
    background: {
      image: {
        id: designData.backgroundImage?.id ?? null,
        url: designData.backgroundImage?.fullSizeUrl ?? null,
      },
      color: designData.backgroundColor,
      size: designData.backgroundSize,
    },
  };

  return { badge, design };
};

// prepare the design payload for the update API call
export const prepare = (badge, design) => {
  const { widgets, background } = design;
  const designData = {
    backgroundColor: background.color,
    backgroundSize: background.size,
  };

  if (background.image?.id) {
    designData.backgroundImage = { id: background.image.id };
  } else {
    designData.backgroundImage = null;
  }

  designData.widgets = widgets.map((widget, i) => {
    const { type, layout, params } = widget;
    const next = {
      type,
      x: layout.x,
      y: layout.y,
      z: i,
      width: layout.w / UNIT_DIVISION[badge.size.unit],
      height: layout.h / UNIT_DIVISION[badge.size.unit],
    };

    if (!widget.internalState.isNew) {
      next.id = widget.id;
    }

    if (params.image?.id) {
      next.backgroundImage = {
        id: params.image.id,
      };
    } else {
      next.backgroundImage = null;
    }

    if (params.foreground?.id) {
      next.foregroundImage = {
        id: params.foreground.id,
      };
    } else {
      next.foregroundImage = null;
    }

    if (params.richText !== null) {
      next.foregroundRichText = params.richText;
    }

    // remove image and foreground from the params as they have their dedicated attributes
    // defined for each widget
    const { image, foreground, richText, internalState, ...rest } = params;

    next.configuration = JSON.stringify(rest);

    return next;
  });

  return {
    appliedTo: badge.appliedTo,
    isBleedEnabled: badge.isBleedEnabled,
    name: badge.name,
    designData,
  };
};

/*
  Any images that have been added or edited may need their stored crop values
  updated.
*/
export const getImageProcessingQueue = (design, origin) => {
  const { widgets, background } = design;

  // filter image widgets that are currently on the page and going to be updated
  const next = widgets.filter(w => TYPE_IMAGE.includes(w.type));
  // filter image widgets that were on the grid before changes
  const prev = origin.widgets.filter(w => TYPE_IMAGE.includes(w.type));

  const extractImg = prop =>
    next
      // check if image for the provided `prop` is cropped or not
      .filter(w => w?.params?.[prop]?.isCropped ?? false)
      // make a new array with the id of the image and its crop parameters
      .map(w => [w.params[prop].id, { ...w.params[prop].crop }]);

  const cropQueue = [...extractImg('image'), ...extractImg('foreground')];

  if (background?.image?.isCropped) {
    // in case background image has been cropped, add to the queue
    cropQueue.push([background.image.id, { ...background.image.crop }]);
  }

  const extractIdx = list =>
    list
      // take id for both background and foreground images
      .flatMap(w => [w.params?.image?.id ?? null, w.params?.foreground?.id ?? null])
      // remove all undefined and nulls
      .filter(id => typeof id !== 'undefined' && id !== null);

  // get the idx for all the images that are currently on the page
  const idx = extractIdx(next);

  const deleteQueue = extractIdx(prev)
    // make a list of all original images that are NOT in the widgets anymore
    .reduce((list, id) => {
      if (idx.includes(id)) {
        return list;
      }

      return [...list, id];
    }, []);

  if (origin.background?.image?.id && origin.background?.image?.id !== background.image?.id) {
    // only add to the delete queue when image existed before but it has been modified
    deleteQueue.push(origin.background.image.id);
  }

  return [cropQueue, deleteQueue];
};

/*
  Any image widgets that have been cloned will need their referenced files cloned
  too, and the new values backfilled into the design we're saving.
*/
export const handleClonedImages = async (eventId, widgets) =>
  Promise.all(
    widgets.map(async widget => {
      if (!TYPE_IMAGE.includes(widget.type) || !widget.internalState.isCloned) {
        return widget;
      }

      const promises = [];

      if (widget?.params?.image?.id) {
        promises.push(duplicate(eventId, widget.params.image.id));
      }

      if (widget?.params?.foreground?.id) {
        promises.push(duplicate(eventId, widget.params.foreground.id));
      }

      const [image, foreground] = await Promise.all(promises);

      const { internalState, ...params } = widget.params;

      return {
        ...widget,
        params: {
          ...params,
          image: { ...widget.params.image, id: image?.id ?? null },
          foreground: { ...widget.params.foreground, id: foreground?.id ?? null },
        },
      };
    }),
  );

export const updateDesign = async (eventId, id, { badge, design }, prevData) => {
  const updatedWidgets = await handleClonedImages(eventId, design.widgets);
  const payload = { ...design, widgets: updatedWidgets };

  const [cropQueue, deleteQueue] = getImageProcessingQueue(payload, prevData.design);

  await Promise.all([
    ...cropQueue.map(args => crop(eventId, ...args)),
    ...deleteQueue.map(img => remove(eventId, img)),
  ]);

  return api(eventId)
    .patch(id, prepare(badge, payload))
    .then(res => res.data.data);
};
